import { pipe } from '@helo/utils'

import { Colors, colorPaletteOptions } from '../../Colors'
import { Options } from '../../Options'
import { RenderPipeline } from '../../RenderPipeline'
import type { ChartBandPack, ChartCartesianOutput } from '../ChartCartesian'
import { ChartCartesianUtils } from '../ChartCartesian'
import type {
  ChartDataPoint,
  ChartSeries,
  ResolvedChartSeries,
} from '../ChartData'
import { ChartDataParts } from '../ChartData/ChartData.parts'
import { ChartLineConstants } from '../ChartLine/ChartLine.constants'
import type { ChartSeriesOutput } from '../ChartSeries'
import { ChartSeriesUtils } from '../ChartSeries'
import type { ChartBarInput, ResolvedChartBarSeries } from './ChartBar'
import { ChartBarConstants } from './ChartBar.constants'
import {
  type ChartBarLayout,
  type ChartBarOrientation,
  chartBarOptions,
} from './ChartBar.options'

export type ChartBarCategoryScale = {
  (value: string): number | undefined
  bandwidth?: () => number
}

export type ChartBarValueScale = (value: number) => number

type ResolveBarSeriesInput = {
  outputSeries: ResolvedChartSeries[]
  inputSeries: ChartSeries[]
  colorByKey: Record<string, string>
  fallbackColor: string
  patternHighlight: string
  barOrientation: ChartBarOrientation
  barLayout: ChartBarLayout
  barSize: number
  barGap: number
  barCornerRadius: number
}

const isStackedLayout = (layout: ChartBarLayout): boolean =>
  layout === 'stacked' || layout === 'stacked100'

const defaultBarSizeFor = (orientation: ChartBarOrientation): number =>
  orientation === 'horizontal'
    ? ChartBarConstants.defaultHorizontalBarSize
    : ChartBarConstants.defaultBarSize

const stackOffsetsFor = (series: ResolvedChartSeries[]): number[][] => {
  const running = new Map<string, number>()
  return series.map(seriesItem =>
    seriesItem.points.map(point => {
      const base = running.get(point.x) ?? 0
      if (point.y != null) {
        running.set(point.x, base + point.y)
      }
      return base
    }),
  )
}

const stackedTotals = (
  series: ResolvedChartSeries[],
): { measureMin: number; measureMax: number } => {
  const totals = new Map<string, number>()
  for (const seriesItem of series) {
    for (const point of seriesItem.points) {
      if (point.y == null || Number.isNaN(point.y)) continue
      totals.set(point.x, (totals.get(point.x) ?? 0) + point.y)
    }
  }
  const values = [...totals.values()]
  return {
    measureMin: values.length ? Math.min(0, ...values) : 0,
    measureMax: values.length ? Math.max(...values) : 0,
  }
}

/**
 * Normalize each category’s measures to share of total (0–100). Keeps
 * `rawPoints` for tooltips; plot `points` become percentages.
 */
const normalizeStacked100 = (
  series: ResolvedChartSeries[],
): ResolvedChartSeries[] => {
  const totals = new Map<string, number>()
  for (const seriesItem of series) {
    for (const point of seriesItem.points) {
      if (point.y == null || Number.isNaN(point.y)) continue
      totals.set(point.x, (totals.get(point.x) ?? 0) + point.y)
    }
  }

  return series.map(seriesItem => ({
    ...seriesItem,
    points: seriesItem.points.map(point => {
      if (point.y == null || Number.isNaN(point.y)) return point
      const total = totals.get(point.x) ?? 0
      return {
        x: point.x,
        y: total > 0 ? (point.y / total) * 100 : 0,
      }
    }),
  }))
}

/** Maps style options → VisX-ready props on each bar series. */
const resolveBarSeries = ({
  outputSeries,
  inputSeries,
  colorByKey,
  fallbackColor,
  patternHighlight,
  barOrientation,
  barLayout,
  barSize,
  barGap,
  barCornerRadius,
}: ResolveBarSeriesInput): ResolvedChartSeries[] => {
  const isBar = ChartSeriesUtils.isVisualization('bar')
  const barSeries = outputSeries.filter(isBar)
  const plotSeries =
    barLayout === 'stacked100' ? normalizeStacked100(barSeries) : barSeries
  const offsets = isStackedLayout(barLayout)
    ? stackOffsetsFor(plotSeries)
    : plotSeries.map(seriesItem => seriesItem.points.map(() => 0))
  const seriesCount = barSeries.length

  let barIndex = 0
  return outputSeries.map((resolvedSeries, index) => {
    if (!isBar(resolvedSeries)) {
      return resolvedSeries
    }

    const inputItem = inputSeries[index]
    const color =
      inputItem?.color ?? colorByKey[resolvedSeries.seriesKey] ?? fallbackColor
    const plotPoints = plotSeries[barIndex]?.points ?? resolvedSeries.points
    const seriesIndex = barIndex
    barIndex += 1

    return {
      ...resolvedSeries,
      visualization: 'bar',
      points: plotPoints,
      ...(barLayout === 'stacked100'
        ? { rawPoints: resolvedSeries.points }
        : {}),
      color,
      fillColor: inputItem?.fillColor ?? color,
      highlightColor: inputItem?.highlightColor ?? patternHighlight,
      barOrientation,
      barLayout,
      barSize,
      barGap,
      barCornerRadius,
      seriesIndex,
      seriesCount,
      stackOffsets: offsets[seriesIndex] ?? plotPoints.map(() => 0),
      pointRadius: ChartLineConstants.defaultPointRadius,
      lineWidth: ChartLineConstants.defaultLineWidth,
    } satisfies ResolvedChartBarSeries
  })
}

const categoryBandwidth = (scale: ChartBarCategoryScale): number =>
  scale.bandwidth == null ? 0 : scale.bandwidth()

const clusterWidth = ({
  seriesCount,
  barSize,
  barGap,
  layout,
}: {
  seriesCount: number
  barSize: number
  barGap: number
  layout: ChartBarLayout
}): number =>
  isStackedLayout(layout) || seriesCount <= 1
    ? barSize
    : barSize * seriesCount + barGap * (seriesCount - 1)

const packCategoryBand = ({
  categoryCount,
  barSize,
  barGap,
  layout,
  seriesCount,
  orientation,
}: {
  categoryCount: number
  barSize: number
  barGap: number
  layout: ChartBarLayout
  seriesCount: number
  orientation: ChartBarOrientation
}): ChartBandPack | undefined => {
  if (categoryCount <= 0) return undefined
  const bandSize = clusterWidth({
    seriesCount,
    barSize,
    barGap,
    layout,
  })
  if (orientation === 'horizontal') {
    return {
      paddingInner: 0,
      paddingOuter: 0,
      bandSize,
    }
  }
  const categoryGap = ChartBarConstants.verticalCategoryGapPx
  const step = bandSize + categoryGap
  return {
    paddingInner: step > 0 ? categoryGap / step : 0,
    paddingOuter: 0,
    bandSize,
    packedLength:
      categoryCount * bandSize + Math.max(0, categoryCount - 1) * categoryGap,
  }
}

const fittedCluster = ({
  bandwidth,
  seriesCount,
  barSize,
  barGap,
  layout,
}: {
  bandwidth: number
  seriesCount: number
  barSize: number
  barGap: number
  layout: ChartBarLayout
}): { size: number; gap: number } => {
  if (bandwidth <= 0) return { size: barSize, gap: barGap }
  if (isStackedLayout(layout) || seriesCount <= 1) {
    return { size: Math.min(barSize, bandwidth), gap: 0 }
  }
  const natural = clusterWidth({
    seriesCount,
    barSize,
    barGap,
    layout,
  })
  const scale = Math.min(1, bandwidth / Math.max(1, natural))
  return { size: barSize * scale, gap: barGap * scale }
}

const clusterStartInBand = ({
  bandStart,
  bandwidth,
  clusterSpan,
}: {
  bandStart: number
  bandwidth: number
  clusterSpan: number
}): number => bandStart + Math.max(0, (bandwidth - clusterSpan) / 2)

/** Stacked layouts only round the outermost (last) segment. */
const cornerRadiusForSegment = (series: ResolvedChartBarSeries): number => {
  if (!isStackedLayout(series.barLayout)) return series.barCornerRadius
  return series.seriesIndex === series.seriesCount - 1
    ? series.barCornerRadius
    : 0
}

export type ChartBarRect = {
  x: number
  y: number
  width: number
  height: number
}

const barRect = ({
  point,
  pointIndex,
  series,
  categoryScale,
  valueScale,
}: {
  point: ChartDataPoint
  pointIndex: number
  series: ResolvedChartBarSeries
  categoryScale: ChartBarCategoryScale
  valueScale: ChartBarValueScale
}): ChartBarRect | null => {
  if (point.y == null) return null

  const bandwidth = categoryBandwidth(categoryScale)
  const bandStart = categoryScale(point.x) ?? 0
  const { size, gap } = fittedCluster({
    bandwidth,
    seriesCount: series.seriesCount,
    barSize: series.barSize,
    barGap: series.barGap,
    layout: series.barLayout,
  })
  const clusterSpan =
    isStackedLayout(series.barLayout) || series.seriesCount <= 1
      ? size
      : size * series.seriesCount + gap * (series.seriesCount - 1)
  const origin = clusterStartInBand({
    bandStart,
    bandwidth,
    clusterSpan,
  })
  const groupedOffset =
    series.barLayout === 'grouped' && series.seriesCount > 1
      ? series.seriesIndex * (size + gap)
      : 0
  const stackBase = series.stackOffsets[pointIndex] ?? 0
  const valueFrom = isStackedLayout(series.barLayout) ? stackBase : 0
  const valueTo = valueFrom + point.y
  const valueFromPx = valueScale(valueFrom)
  const valueToPx = valueScale(valueTo)
  const thickness = Math.abs(valueToPx - valueFromPx)
  const valueStart = Math.min(valueFromPx, valueToPx)
  const rect =
    series.barOrientation === 'horizontal'
      ? {
          x: valueStart,
          y: origin + groupedOffset,
          width: thickness,
          height: size,
        }
      : {
          x: origin + groupedOffset,
          y: valueStart,
          width: size,
          height: thickness,
        }

  if (
    !Number.isFinite(rect.x) ||
    !Number.isFinite(rect.y) ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height)
  ) {
    return null
  }

  return rect
}

const isResolvedBarSeries = (
  series: ResolvedChartSeries,
): series is ResolvedChartBarSeries =>
  series.visualization === 'bar' &&
  'barOrientation' in series &&
  'seriesIndex' in series &&
  'stackOffsets' in series

const barFeatureOptions = {
  ...colorPaletteOptions,
  ...chartBarOptions,
}

/** Resolve bar and palette options from override, then content-node state. */
const setupInput = () =>
  RenderPipeline.updateInput((input, ctx) =>
    pipe(
      input,
      Options.fallback(barFeatureOptions, ChartDataParts.getChartState()(ctx)),
      Options.applyDefaults(barFeatureOptions),
      Options.validate(barFeatureOptions),
    ),
  )

type ChartBarSetupOutput = ChartSeriesOutput & Partial<ChartCartesianOutput>

/** Map bar style options onto resolved series. */
const setBarSeries = () =>
  RenderPipeline.updateOutput(
    (output: ChartBarSetupOutput, ctx: { input: ChartBarInput }) => {
      const barOrientation =
        ctx.input.barOrientation ?? ChartBarConstants.defaultBarOrientation
      if (
        ChartSeriesUtils.hasLineAndHorizontalBar(output.series, barOrientation)
      ) {
        throw new Error(
          'Horizontal bar charts cannot be combined with line series',
        )
      }

      const barSeries = output.series.filter(
        ChartSeriesUtils.isVisualization('bar'),
      )
      if (barSeries.length === 0) {
        return {}
      }

      const barLayout =
        ctx.input.barLayout ?? ChartBarConstants.defaultBarLayout
      const barSize = ctx.input.barSize ?? defaultBarSizeFor(barOrientation)
      const pattern = Colors.getPattern(
        Colors.mapColorPalette(ctx.input.colorPalette),
      )
      const inputSeries = (ctx.input._series ?? []) as ChartSeries[]
      const series = resolveBarSeries({
        outputSeries: output.series,
        inputSeries,
        colorByKey: ChartSeriesUtils.assignColors(
          output.series.map(seriesItem => seriesItem.seriesKey),
          Colors.mapColorPalette(ctx.input.colorPalette),
          ChartSeriesUtils.colorOverridesFromInput(inputSeries, output.series),
        ),
        fallbackColor: pattern.colors[0]!,
        patternHighlight: Colors.getHighlight(pattern),
        barOrientation,
        barLayout,
        barSize,
        barGap: ctx.input.barGap ?? ChartBarConstants.defaultBarGap,
        barCornerRadius:
          ctx.input.barCornerRadius ?? ChartBarConstants.defaultCornerRadius,
      })

      return { series }
    },
  )

/** Stacked domain, band packing, and plot orientation. */
const setStackedCartesian = () =>
  RenderPipeline.updateOutput(
    (output: ChartBarSetupOutput, ctx: { input: ChartBarInput }) => {
      const barSeries = output.series.filter(
        ChartSeriesUtils.isVisualization('bar'),
      )
      if (barSeries.length === 0 || output.cartesian == null) {
        return {}
      }

      const barOrientation =
        ctx.input.barOrientation ?? ChartBarConstants.defaultBarOrientation
      const barLayout =
        ctx.input.barLayout ?? ChartBarConstants.defaultBarLayout
      const barSize = ctx.input.barSize ?? defaultBarSizeFor(barOrientation)
      const cartesian = output.cartesian
      const stackedExtent =
        barLayout === 'stacked' ? stackedTotals(barSeries) : null
      const stackedDomain =
        barLayout === 'stacked100'
          ? {
              yDomain: ChartBarConstants.stacked100Domain,
              yTicks: ChartBarConstants.stacked100Ticks,
            }
          : stackedExtent == null
            ? null
            : ChartCartesianUtils.resolveYDomainAndTicks({
                measureMin: Math.min(
                  stackedExtent.measureMin,
                  cartesian.measureMin,
                ),
                measureMax: Math.max(
                  stackedExtent.measureMax,
                  cartesian.measureMax,
                ),
                yAxisMin: cartesian.min,
                yAxisMax: cartesian.max,
                tickCount: cartesian.tickCount,
              })
      const bandPack = packCategoryBand({
        categoryCount: cartesian.xDomain.length,
        barSize,
        barGap: ctx.input.barGap ?? ChartBarConstants.defaultBarGap,
        layout: barLayout,
        seriesCount: barSeries.length,
        orientation: barOrientation,
      })

      return {
        cartesian: {
          ...cartesian,
          plotOrientation: barOrientation,
          categoryScale: 'band' as const,
          ...(bandPack == null ? {} : { bandPack }),
          ...(stackedDomain == null
            ? {}
            : {
                measureMin: stackedDomain.yDomain[0],
                measureMax: stackedDomain.yDomain[1],
                yDomain: stackedDomain.yDomain,
                yTicks: stackedDomain.yTicks,
              }),
        },
      }
    },
  )

export const ChartBarInputParts = {
  setupInput,
}

export const ChartBarOutputParts = {
  setBarSeries,
  setStackedCartesian,
}

export const ChartBarUtils = {
  isStackedLayout,
  defaultBarSizeFor,
  stackOffsetsFor,
  stackedTotals,
  normalizeStacked100,
  resolveBarSeries,
  categoryBandwidth,
  packCategoryBand,
  cornerRadiusForSegment,
  barRect,
  isResolvedBarSeries,
}
